/**
 * Whether a visit can be finished with what the operator has entered so far.
 *
 * beaut-555. A visit can be owed in two currencies, and this check used to
 * read them from two different places: money from the live form, minutes only
 * from payments already saved on the server. So typing the money was enough to
 * offer "save and complete", but typing the minutes was not — the operator had
 * to save first to reveal the button that does the finishing, which is the one
 * thing saving alone does not do.
 *
 * Both are now read the same way: what is already settled, plus what is on the
 * form in front of you.
 *
 * This does NOT promise the save will succeed. The server refuses minutes the
 * customer does not hold ("მომხმარებელს არ აქვს საკმარისი წუთები",
 * visit.service.ts), and that stays the authority on the balance — this only
 * decides whether the button is worth offering.
 */

/** Payment types that may complete a visit with any amount, including none. */
const SETTLED_WITHOUT_MONEY = [6, 11];

export interface CompletionAmounts {
  paymentType?: number | string | null;
  /** The money fields as they stand on the form. */
  cash?: number | string | null;
  card?: number | string | null;
  balance?: number | string | null;
  bankTransfer?: number | string | null;
  barter?: number | string | null;
  consignation?: number | string | null;
  /** The minutes field as it stands on the form — the half that was ignored. */
  minutes?: number | string | null;
}

export interface CompletionTotals {
  totalPayPrice: number;
  totalPaid: number;
  totalConsignation: number;
  totalMinutes: number;
  totalMinutesPaid: number;
}

const toNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export function canCompleteVisit(form: CompletionAmounts, totals: CompletionTotals): boolean {
  if (SETTLED_WITHOUT_MONEY.includes(Number(form?.paymentType))) {
    return true;
  }

  // Read the individual fields rather than `price`, which goes stale.
  const currentAmount =
    toNumber(form?.cash) +
    toNumber(form?.card) +
    toNumber(form?.balance) +
    toNumber(form?.bankTransfer) +
    toNumber(form?.barter) +
    toNumber(form?.consignation);

  const currentMinutes = toNumber(form?.minutes);

  const hasMoney = totals.totalPayPrice > 0;
  const hasMinutes = totals.totalMinutes > 0;

  if (!hasMoney && !hasMinutes) {
    return true;
  }

  const moneyOk =
    !hasMoney ||
    totals.totalPaid + totals.totalConsignation + currentAmount >= totals.totalPayPrice;

  const minutesOk = !hasMinutes || totals.totalMinutesPaid + currentMinutes >= totals.totalMinutes;

  return moneyOk && minutesOk;
}
